import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import { useBgReport, useBgJob } from './BackgroundJobs';
import AddressCascade from './AddressCascade';
import staffTopoFetcher from './staffTopoFetcher';
import CoperturaMatrice from './CoperturaMatrice';
import { motivoSalesMate } from '../salesmateMotivo';

// Verifica Copertura SalesMate per indirizzo — /staff-ai/copertura-sm (coda pool SalesMate di sistema).
const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

const TERMIN = ['completato', 'errore', 'annullato'];
const EMPTY = { provincia: '', comune: '', via: '', civico: '' };

export default function CoperturaSmDialog({ open, onClose }) {
  const [addr, setAddr] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);
  const clear = () => { clearInterval(poll.current); setAddr(EMPTY); setBusy(false); setJob(null); setJobId(null); };

  const avvia = async () => {
    if (!addr.comune.trim() || !addr.via.trim()) return;
    clearInterval(poll.current); setBusy(true); setJob(null);
    try {
      const r = await axios.post(`${API}/staff-ai/copertura-sm`, { provincia: addr.provincia, comune: addr.comune, indirizzo: addr.via, civico: addr.civico });
      const id = r.data.id; setJobId(id);
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/staff-ai/copertura-sm/${id}`);
          setJob(s.data);
          if (TERMIN.includes(s.data.stato)) { clearInterval(poll.current); setBusy(false); }
        } catch { /* ritenta */ }
      }, 1500);
    } catch (e) { setBusy(false); setJob({ stato: 'errore', errore: e.response?.data?.detail || 'Errore avvio' }); }
  };

  const ris = job?.risultato;
  const errTxt = motivoSalesMate(job?.errore) || (ris && ris.trovato === false && (motivoSalesMate(ris.errore) || 'Indirizzo non trovato su SalesMate'));
  const trovato = ris && ris.trovato === true;
  const errore = job?.stato === 'errore' || (job?.stato === 'completato' && ris && ris.trovato === false);
  const done = !!job && TERMIN.includes(job.stato);
  const summary = errore ? (errTxt || 'non trovato') : trovato ? 'indirizzo trovato' : (done ? 'non trovato' : null);

  useBgReport('copsm', { label: 'Copertura SalesMate', color: '#06b6d4', iconKey: 'copsm' },
    { open, running: busy, done: done && !errore, error: errore, summary, jobId, active: busy || !!job });

  const entry = useBgJob('copsm');
  const had = useRef(false);
  useEffect(() => { if (entry) had.current = true; else if (had.current) { had.current = false; clear(); } }, [entry]); // eslint-disable-line
  useEffect(() => {
    if (open && !busy && !job && entry && entry.jobId) {
      axios.get(`${API}/staff-ai/copertura-sm/${entry.jobId}`).then((r) => { if (TERMIN.includes(r.data.stato)) setJob(r.data); }).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  return createPortal(
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-dialog" onClick={(e) => e.stopPropagation()} style={{ width: 720 }}>
        <div className="bulk-content">
          <div className="bulk-head" style={{ marginBottom: 14 }}>
            <span className="bulk-title">Copertura SalesMate</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {busy && (
                <button onClick={onClose} title="Metti in background: la verifica continua, riapri dal pulsante in basso a destra"
                  style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.22)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>⤓ Background</button>
              )}
              <button className="bulk-x" onClick={onClose}><IconClose /></button>
            </div>
          </div>

          <div style={{ marginBottom: 12, opacity: busy ? 0.55 : 1, pointerEvents: busy ? 'none' : 'auto' }}>
            <AddressCascade variant="desktop" inputClass="topo-in" fetcher={staffTopoFetcher} onChange={setAddr} rowLayout />
          </div>
          <button onClick={avvia} disabled={busy || !addr.comune.trim() || !addr.via.trim()}
            style={{ width: '100%', padding: '10px 16px', borderRadius: 9, border: 0, fontWeight: 800, fontSize: 13, cursor: busy ? 'default' : 'pointer', opacity: busy || !addr.comune.trim() || !addr.via.trim() ? 0.6 : 1, color: '#fff', background: 'linear-gradient(180deg,#22d3ee,#0891b2)' }}>
            {busy ? 'In coda / verifica…' : 'Verifica copertura'}
          </button>

          {busy ? (
            <div style={{ padding: '12px 0 4px' }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', marginBottom: 8 }}>
                {job?.stato === 'in_coda' ? `In coda${job.posizione ? ` · posizione ${job.posizione}` : ''}…` : 'Verifica sul pool SalesMate…'}
              </div>
              <div className="indaga-track"><div className="indaga-fill" style={{ width: '100%', background: 'linear-gradient(90deg,#0891b2,#22d3ee,#0891b2)' }} /></div>
            </div>
          ) : job ? (
            errTxt ? <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 12 }}>{errTxt}</div> : (
              <div style={{ marginTop: 12 }}>
                {trovato ? (
                  <CoperturaMatrice d={ris} />
                ) : (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: '#fff', background: '#9ca3af', padding: '6px 14px', borderRadius: 10 }}>Non trovato</div>
                )}
                {ris?.code_istat && <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginTop: 8 }}>Codice ISTAT {ris.code_istat}</div>}
              </div>
            )
          ) : entry && entry.status !== 'running' ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', padding: '12px 0 0' }}>Ultimo esito: <b style={{ color: '#fff' }}>{entry.summary || '—'}</b> <span style={{ color: 'rgba(255,255,255,.45)' }}>(fai una nuova verifica sopra)</span></div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '12px 0 0' }}>
              Inserisci l'indirizzo: la copertura SalesMate entra in coda sul pool di sistema e verifica la vendibilità sul portale.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
